import { useFeatureFlag } from "@harnessio/ff-react-client-sdk";
import { RowData } from "../../../../types";
import { FEATURE_FLAG_KEYS, SCHEMA_TITLES } from "../../constants";
import { canConvertARRecordScheduleType } from "../../helpers";

interface UseHeaderFeatureFlagsParams {
  setupName: string;
  isReadOnly: boolean | undefined;
  rowData: RowData;
}

export interface HeaderFeatureFlags {
  isPrivlibFeatureEnabled: boolean;
  isCopyGroupCodesFeatureEnabled: boolean;
  isCopyPOUnitsOfMeasureFeatureEnabled: boolean;
  isARRecordMigrationFeatureEnabled: boolean;
  isFileUploadFeatureEnabled: boolean;
  isFormResetFeatureFlagEnabled: boolean;
  isSaveOnChangeForExistingRecordsFlagEnabled: boolean;
}

export function useHeaderFeatureFlags({
  setupName,
  isReadOnly,
  rowData,
}: UseHeaderFeatureFlagsParams): HeaderFeatureFlags {
  const isPrivlibFeatureEnabled = useFeatureFlag(
    FEATURE_FLAG_KEYS.ENABLE_PRIVLIB_SETUP_MANAGEMENT,
    false,
  );

  const isCopyGroupCodesFeatureEnabled =
    useFeatureFlag(FEATURE_FLAG_KEYS.ENABLE_COPY_GROUP_CODES, false) &&
    setupName === SCHEMA_TITLES.REPORT_GROUP_CODES;

  const isCopyPOUnitsOfMeasureFeatureEnabled =
    useFeatureFlag(FEATURE_FLAG_KEYS.ENABLE_COPY_PO_UNITS_OF_MEASURE, false) &&
    setupName === SCHEMA_TITLES.PO_UNITS_OF_MEASURE;

  const isARRecordMigrationFeatureEnabled =
    useFeatureFlag(FEATURE_FLAG_KEYS.ENABLE_AR_RECORD_CONVERSION, false) &&
    setupName === SCHEMA_TITLES.ACCOUNTS_RECEIVABLE &&
    canConvertARRecordScheduleType(isReadOnly, rowData);

  const isFileUploadFeatureEnabled =
    useFeatureFlag(FEATURE_FLAG_KEYS.BULK_UPLOAD, false) &&
    (setupName === SCHEMA_TITLES.CHART_OF_ACCOUNTS ||
      setupName === SCHEMA_TITLES.PRICING_MATRIX);

  const isFormResetFeatureFlagEnabled = useFeatureFlag(
    FEATURE_FLAG_KEYS.ENABLE_FORM_RESET,
    false,
  );

  const isSaveOnChangeForExistingRecordsFlagEnabled = useFeatureFlag(
    FEATURE_FLAG_KEYS.ENABLE_SAVE_ON_CHANGE_FOR_EXISTING_RECORDS,
    false,
  );

  return {
    isPrivlibFeatureEnabled,
    isCopyGroupCodesFeatureEnabled,
    isCopyPOUnitsOfMeasureFeatureEnabled,
    isARRecordMigrationFeatureEnabled,
    isFileUploadFeatureEnabled,
    isFormResetFeatureFlagEnabled,
    isSaveOnChangeForExistingRecordsFlagEnabled,
  };
}
